import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Hash } from 'lucide-react';
import Lightbox from '@/components/Lightbox';
import BookingHint from '@/components/BookingHint';
import { useAppStore } from '../store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { HolidayHome, BookingRequest } from '../types';

const HomesPage = () => {
  const { homes, createBooking, checkAvailability, currentUser } = useAppStore();
  const [selectedHome, setSelectedHome] = useState<HolidayHome | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<{ available: boolean; message?: string } | null>(null);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState<{ referenceNumber: string; homeName: string } | null>(null);
  const [lightboxHome, setLightboxHome] = useState<HolidayHome | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Respect per-user home access: admins see all, others only their allowed homes (if set)
  const accessibleHomes = currentUser?.isAdmin || !currentUser?.allowedHomeIds?.length
    ? homes
    : homes.filter(h => currentUser.allowedHomeIds!.includes(h.id));

  const filteredHomes = accessibleHomes.filter(home =>
    home.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    home.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDateChange = async (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setBookingError(null);
    setAvailabilityStatus(null);

    const isGuestUser = !currentUser?.isAdmin && !currentUser?.isFamily && !currentUser?.isFamilyMember;
    if (isGuestUser && newStartDate && newEndDate) {
      const nights = calculateNights(newStartDate, newEndDate);
      if (nights < 3) {
        setBookingError('Mindestaufenthalt für Gäste: 3 Nächte.');
        return;
      }
    }

    // Check availability when both dates are set
    if (newStartDate && newEndDate && selectedHome) {
      setIsCheckingAvailability(true);
      try {
        const result = await checkAvailability(
          selectedHome.id,
          new Date(newStartDate),
          new Date(newEndDate)
        );
        if (result.available) {
          setAvailabilityStatus({ available: true, message: 'Dates are available!' });
        } else {
          const message = result.blockedReason || 'These dates are not available';
          setAvailabilityStatus({ available: false, message });
        }
      } catch {
        setAvailabilityStatus({ available: false, message: 'Unable to check availability' });
      } finally {
        setIsCheckingAvailability(false);
      }
    }
  };

  const submitBooking = async () => {
    if (!selectedHome || !startDate || !endDate || !currentUser) return;

    setIsSubmitting(true);
    setBookingError(null);

    const bookingRequest: BookingRequest = {
      homeId: selectedHome.id,
      userId: currentUser.id,
      startDate: new Date(startDate),
      endDate: new Date(endDate),
    };

    const result = await createBooking(bookingRequest);

    if (result.success && result.referenceNumber) {
      setBookingSuccess({
        referenceNumber: result.referenceNumber,
        homeName: selectedHome.name,
      });
      setIsBookingModalOpen(false);
      setSelectedHome(null);
      setStartDate('');
      setEndDate('');
      setAvailabilityStatus(null);
    } else {
      setBookingError(result.error || 'Failed to create booking');
    }

    setIsSubmitting(false);
  };

  const calculateNights = (start: string, end: string) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getRate = (home: HolidayHome) => {
    const seasonal = home.pricingStructure.seasonalConfig;
    if (seasonal) {
      return seasonal.highSeasonRates.weekRate;
    }
    return currentUser?.isFamily
      ? home.pricingStructure.userTypeOverrides?.family || home.pricingStructure.weekdayRate
      : home.pricingStructure.userTypeOverrides?.guest || home.pricingStructure.weekdayRate;
  };

  const calculatePrice = (home: HolidayHome, start: string, end: string) => {
    const nights = calculateNights(start, end);
    if (nights === 0) return 0;
    return Math.round(getRate(home) * nights);
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <>
      {/* Page Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Unterkünfte</h1>
        <p className="text-muted-foreground text-lg">
          Einzigartige Ferienhäuser für unvergessliche Auszeiten
        </p>
      </div>

      {/* Search Section */}
      <div className="flex justify-center mb-8">
        <Input
          placeholder="Search properties..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {/* Property Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {filteredHomes.map((home) => {
          const coverIndex = Math.min(home.featuredImageIndex ?? 0, Math.max(0, home.images.length - 1));
          const coverImage = home.images[coverIndex];
          return (
            <Card key={home.id} className="overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
              {/* Image with counter */}
              <button
                type="button"
                className="relative block w-full group"
                onClick={() => {
                  setLightboxHome(home);
                  setLightboxIndex(coverIndex);
                }}
                aria-label={`${home.name} Bilder ansehen`}
              >
                <img
                  src={coverImage}
                  alt={home.name}
                  className="w-full h-64 object-cover transition-transform group-hover:scale-[1.02]"
                />
                {home.images.length > 1 && (
                  <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-md font-medium">
                    {home.images.length} Fotos
                  </span>
                )}
              </button>

              <CardContent className="p-4 flex-1 space-y-2">
                <h3 className="text-lg font-bold leading-snug">{home.name}</h3>
                {(home.shortDescription || home.description) && (
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {home.shortDescription || home.description}
                  </p>
                )}
              </CardContent>

              <CardFooter className="p-4 pt-3 border-t">
                <Button asChild className="w-full">
                  <Link to={`/homes/${home.id}`}>Mehr Infos</Link>
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {lightboxHome && (
        <Lightbox
          images={lightboxHome.images}
          startIndex={lightboxIndex}
          alt={lightboxHome.name}
          onClose={() => setLightboxHome(null)}
        />
      )}

      {/* Booking Modal */}
      <Dialog open={isBookingModalOpen} onOpenChange={setIsBookingModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Book {selectedHome?.name}</DialogTitle>
            <DialogDescription>
              {selectedHome?.description}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="check-in">Check-in Date</Label>
              <Input
                id="check-in"
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange(e.target.value, endDate)}
                min={getTodayDate()}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="check-out">Check-out Date</Label>
              <Input
                id="check-out"
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange(startDate, e.target.value)}
                min={startDate || getTodayDate()}
              />
            </div>

            {/* Availability Status */}
            {isCheckingAvailability && (
              <div className="text-sm text-muted-foreground">
                Checking availability...
              </div>
            )}

            {availabilityStatus && !isCheckingAvailability && (
              <div className={`text-sm p-3 rounded-md ${
                availabilityStatus.available
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {availabilityStatus.message}
              </div>
            )}

            {/* Booking Error */}
            {bookingError && (
              <div className="text-sm p-3 rounded-md bg-red-50 text-red-700 border border-red-200">
                {bookingError}
              </div>
            )}

            {startDate && endDate && selectedHome && availabilityStatus?.available && (
              <>
                <div className="bg-muted rounded-md p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{calculateNights(startDate, endDate)} {calculateNights(startDate, endDate) === 1 ? 'Nacht' : 'Nächte'}</span>
                    <span>CHF {calculatePrice(selectedHome, startDate, endDate)}</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>Total</span>
                    <span>CHF {calculatePrice(selectedHome, startDate, endDate)}</span>
                  </div>
                </div>
                <BookingHint />
              </>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsBookingModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={submitBooking}
              disabled={!startDate || !endDate || isSubmitting || !availabilityStatus?.available}
            >
              {isSubmitting ? 'Booking...' : 'Confirm Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={!!bookingSuccess} onOpenChange={() => setBookingSuccess(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <DialogTitle>Booking Confirmed!</DialogTitle>
            </div>
            <DialogDescription>
              Your booking for {bookingSuccess?.homeName} has been submitted and is pending approval.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-muted rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">Your booking reference</p>
              <div className="flex items-center justify-center gap-2 text-2xl font-mono font-bold text-primary">
                <Hash className="h-5 w-5" />
                {bookingSuccess?.referenceNumber}
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center mt-4">
              Save this reference number. You'll receive a notification when your booking is approved.
            </p>
            {(() => {
              const home = homes.find(h => h.name === bookingSuccess?.homeName);
              if (home?.checkInInstructions) {
                return (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">Check-in Instructions</p>
                    <p className="text-sm text-blue-700 whitespace-pre-wrap">{home.checkInInstructions}</p>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setBookingSuccess(null)}>
              Close
            </Button>
            <Button asChild>
              <Link to="/bookings">View My Bookings</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HomesPage;
